import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

/** Static paths — must match routeConfig (avoids circular import: helpers → config → PageLayout → Breadcrumb). */
const HOME_PATH = '/';
const VENUE_LOCATIONS_PATH = '/admin/locations';

const isObjectIdSegment = (segment) => /^[a-f\d]{24}$/i.test(segment);

const GEO_CRUMBS = [
  { segment: 'countries', label: 'Countries', path: '/admin/geo/countries' },
  { segment: 'regions', label: 'Regions', path: '/admin/geo/regions' },
  { segment: 'city-regions', label: 'City regions', path: '/admin/geo/city-regions' },
  { segment: 'cities', label: 'Cities', path: '/admin/geo/cities' },
  { segment: 'areas', label: 'Areas', path: '/admin/geo/areas' },
  { segment: 'neighbourhoods', label: 'Neighbourhoods', path: '/admin/geo/neighbourhoods' },
];

const GEO_SPECIAL = {
  import: { label: 'Geo import', path: '/admin/geo/import' },
};

const ADMIN_HUB = VENUE_LOCATIONS_PATH;
const GEO_HUB = '/admin/geo/countries';

const buildCrumbs = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const items = [{ label: 'Home', to: HOME_PATH }];

  if (segments[0] === 'admin' && segments[1] === 'locations') {
    items.push({ label: 'Admin', to: ADMIN_HUB });
    items.push({ label: 'Venue locations', to: VENUE_LOCATIONS_PATH });
    if (segments[2] && isObjectIdSegment(segments[2])) {
      items.push({ label: 'Location detail', to: null });
    }
    return items;
  }

  if (segments[0] === 'admin' && segments[1] === 'geo') {
    items.push({ label: 'Admin', to: ADMIN_HUB });
    items.push({ label: 'Geo', to: GEO_HUB });

    const geoSegment = segments[2];
    if (geoSegment && GEO_SPECIAL[geoSegment]) {
      items.push({ label: GEO_SPECIAL[geoSegment].label, to: GEO_SPECIAL[geoSegment].path });
      return items;
    }
    if (geoSegment) {
      const geoIndex = GEO_CRUMBS.findIndex((c) => c.segment === geoSegment);
      if (geoIndex >= 0) {
        GEO_CRUMBS.slice(0, geoIndex + 1).forEach((item) => {
          items.push({ label: item.label, to: item.path });
        });
        return items;
      }
      items.push({ label: geoSegment.replace(/-/g, ' '), to: null });
    }
    return items;
  }

  segments.forEach((segment, index) => {
    if (segment === 'admin') {
      if (index < segments.length - 1) {
        items.push({ label: 'Admin', to: ADMIN_HUB });
      }
      return;
    }
    if (segment === 'geo') {
      items.push({ label: 'Geo', to: GEO_HUB });
      return;
    }
    if (isObjectIdSegment(segment)) {
      items.push({ label: 'Detail', to: null });
      return;
    }
    const knownGeo = GEO_CRUMBS.find((c) => c.segment === segment);
    if (knownGeo) {
      items.push({ label: knownGeo.label, to: knownGeo.path });
      return;
    }
    const isLast = index === segments.length - 1;
    items.push({
      label: segment.replace(/-/g, ' '),
      to: isLast ? null : `/${segments.slice(0, index + 1).join('/')}`,
    });
  });

  return items;
};

const Breadcrumb = () => {
  const { pathname } = useLocation();
  const crumbs = useMemo(() => buildCrumbs(pathname), [pathname]);

  return (
    <nav className="mb-4" aria-label="Breadcrumb">
      <ol className="list-reset flex flex-wrap items-center text-sm text-gray-600">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <li key={`${crumb.label}-${index}`} className="flex items-center">
              {index > 0 && <span className="mx-2 text-gray-400">/</span>}
              {isLast || !crumb.to ? (
                <span className="text-gray-900 font-medium">{crumb.label}</span>
              ) : (
                <Link to={crumb.to} className="text-blue-600 hover:text-blue-700 hover:underline">
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
